import React, { useEffect, useState } from "react";
import ReusableTable from "../components/ReusableTable";
import { getApiUrl } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UserManagement = () => {
  // User list states
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");

  // Order history states (for modal)
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedUserOrders, setSelectedUserOrders] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [orderPage, setOrderPage] = useState(1);
  const [orderTotalPages, setOrderTotalPages] = useState(1);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderSearch, setOrderSearch] = useState([]);

  // Fetch all users
  const fetchUsers = async (page = 1) => {
    try {
      const res = await fetch(getApiUrl(`user-list?page=${page}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (data.success) {
        setUsers(data.data);
        setFilteredUsers(data.data);
        setCurrentPage(data.pagination.currentPage);
        setTotalPages(data.pagination.totalPages);
        setTotalUsers(data.pagination.totalOrders);
      }
    } catch (error) {
      toast.error("Failed to load users");
    }
  };

  // Fetch selected user's orders
  const fetchUserOrders = async (userId, page = 1) => {
    try {
      const res = await fetch(getApiUrl(`user-orders/${userId}?page=${page}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (data.success) {
        setSelectedUserOrders(data.data);
        setOrderPage(data.pagination.currentPage);
        setOrderTotalPages(data.pagination.totalPages);
        setOrderTotal(data.pagination.totalOrders);
        setSelectedUser({ userId, ...data.data[0]?.userId });
      }
    } catch (error) {
      toast.error("Failed to load user orders");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // User search filter
  useEffect(() => {
    let result = [...users];
    if (search) {
      const term = search.toLowerCase().trim();
      result = result.filter(
        (u) =>
          (u?.name || "").toLowerCase().includes(term) ||
          (u?.email || "").toLowerCase().includes(term)
      );
    }
    setFilteredUsers(result);
  }, [search, users]);

  // User table columns
  const userColumns = [
    { name: "Si No", selector: (row, index) => filteredUsers.length - index, width: "80px" },
    { name: "Name", selector: (row) => row.name || "-", width: "150px" },
    { name: "Phone", selector: (row) => row.phone || "-", width: "140px" },
    { name: "Email", selector: (row) => row.email || "-", width: "220px" },
    { name: "Type", selector: (row) => row.userType || "-", width: "120px" },
    {
      name: "Actions",
      cell: (row) => (
        <button
          onClick={() => {
            setShowOrderModal(true);
            fetchUserOrders(row._id, 1);
          }}
          style={{
            padding: "4px 12px",
            fontSize: "12px",
            borderRadius: "6px",
            border: "none",
            background: "#3b82f6",
            color: "white",
            cursor: "pointer",
          }}
        >
          View Orders
        </button>
      ),
      width: "140px",
      right: true,
    },
  ];

  // Order table columns (same as Ordermanagement)
  const orderColumns = [
    { name: "Si No", selector: (row, index) => selectedUserOrders.length - index, width: "80px" },
    {
      name: "Order Ref",
      cell: (row) => (
        <span style={{ background: "#eef2ff", color: "#4338ca", padding: "2px 6px", borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>
          {row.orderReference || "-"}
        </span>
      ),
      width: "140px",
    },
    {
      name: "Products",
      cell: (row) => (
        <ul className="list-unstyled mb-0" style={{ maxHeight: "60px", overflow: "auto", fontSize: "12px" }}>
          {row.products.slice(0, 2).map((p, idx) => (
            <li key={idx}>{p.name}</li>
          ))}
        </ul>
      ),
      width: "160px",
    },
    { name: "Amount", selector: (row) => `₹${row.amount}`, width: "110px" },
    {
      name: "Status",
      cell: (row) => {
        let badgeClass = "";
        switch (row.paymentStatus) {
          case "SUCCESS": badgeClass = "bg-success"; break;
          case "PENDING": badgeClass = "bg-warning text-dark"; break;
          case "FAILED": badgeClass = "bg-danger"; break;
          default: badgeClass = "bg-secondary";
        }
        return <span className={`badge ${badgeClass}`}>{row.paymentStatus}</span>;
      },
      width: "110px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <button style={{ padding: "2px 6px", fontSize: "11px", borderRadius: "4px", background: "#10b981", color: "white", border: "none" }}>
          View
        </button>
      ),
      width: "100px",
      right: true,
    },
  ];

  return (
    <div className="container-fluid mt-4">
      <ToastContainer />
      
      {/* ✅ USER LIST SECTION */}
      <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
        <h3 className="fw-bold">User Management</h3>
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: "300px" }}
          />
        </div>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-0">
          <ReusableTable
            columns={userColumns}
            data={filteredUsers}
            title="All Users"
            currentPage={currentPage}
            totalPages={totalPages}
            totalOrders={totalUsers}
            onChangePage={fetchUsers}
          />
        </div>
      </div>

      {/* ✅ ORDER HISTORY MODAL */}
      {showOrderModal && (
        <div
          className="modal show fade d-block"
          style={{ background: "rgba(0,0,0,.5)" }}
          onClick={() => setShowOrderModal(false)}
        >
          <div className="modal-dialog modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedUser?.name}'s Order History ({orderTotal} orders)
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowOrderModal(false)}
                />
              </div>
              <div className="modal-body p-0">
                <div className="d-flex justify-content-between p-3 border-bottom">
                  <span>Phone: {selectedUser?.phone}</span>
                  <span>Success Orders Only</span>
                </div>
                <ReusableTable
                  columns={orderColumns}
                  data={selectedUserOrders}
                  title={`Orders for ${selectedUser?.name}`}
                  currentPage={orderPage}
                  totalPages={orderTotalPages}
                  totalOrders={orderTotal}
                  onChangePage={(page) => fetchUserOrders(selectedUser.userId, page)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
